import React, { useEffect, useRef, useState } from "react";

// A minimal, dependency-free scratch-off canvas: drag/touch to reveal the
// content underneath. Replaces `react-scratchcard`, whose propTypes still
// reference `React.PropTypes` (removed since React 16) and crashes on import
// under React 18.
const ScratchCanvas = ({
  width,
  height,
  image,
  overlayImage,
  fallbackImage,
  fallbackOverlayImage,
  finishPercent = 60,
  brushRadius = 20,
  onScratchStart,
  onComplete,
  children,
}) => {
  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);
  const lastPointRef = useRef(null);
  const hasStartedRef = useRef(false);
  const hasCompletedRef = useRef(false);
  const [loaded, setLoaded] = useState(false);
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    setLoaded(false);
    setFinished(false);
    hasStartedRef.current = false;
    hasCompletedRef.current = false;
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Reset drawing state before repainting; never carry destination-out over.
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    let disposed = false;
    const loadImage = (source) => new Promise((resolve, reject) => {
      const img = new Image();
      // Uploaded covers live on the API origin. CORS keeps getImageData usable.
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = source;
    });
    const paint = async () => {
      let cover;
      let overlaySource = overlayImage;
      try { cover = await loadImage(image); }
      catch {
        overlaySource = fallbackOverlayImage;
        if (fallbackImage) {
          try { cover = await loadImage(fallbackImage); } catch { /* Solid cover below remains scratchable. */ }
        }
      }
      if (disposed) return;
      ctx.fillStyle = "#111744";
      ctx.fillRect(0, 0, width, height);
      if (cover) {
        const side = Math.min(cover.naturalWidth, cover.naturalHeight);
        ctx.drawImage(cover, (cover.naturalWidth - side) / 2, (cover.naturalHeight - side) / 2,
          side, side, -3, -3, width + 6, height + 6);
      } else {
        ctx.fillStyle = "#f5c400";
        ctx.font = "bold 16px sans-serif";
        ctx.textAlign = "center";
        ctx.fillText("SCRATCH & WIN", width / 2, height / 2);
      }
      if (overlaySource) {
        try {
          const overlay = await loadImage(overlaySource);
          if (disposed) return;
          ctx.drawImage(overlay, 0, 0, width, height);
        } catch { /* The base cover is still usable. */ }
      }
      if (!disposed) setLoaded(true);
    };
    paint();
    return () => { disposed = true; };
  }, [image, overlayImage, fallbackImage, fallbackOverlayImage, width, height]);

  const getPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches && e.touches[0];
    const clientX = touch ? touch.clientX : e.clientX;
    const clientY = touch ? touch.clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const scratchAt = (point) => {
    const ctx = canvasRef.current.getContext("2d");
    ctx.globalCompositeOperation = "destination-out";
    ctx.beginPath();
    ctx.arc(point.x, point.y, brushRadius, 0, Math.PI * 2, false);
    ctx.fill();
  };

  const getClearedPercent = () => {
    const ctx = canvasRef.current.getContext("2d");
    const pixels = ctx.getImageData(0, 0, width, height);
    const stride = 32;
    let cleared = 0;
    let total = 0;
    for (let i = 0; i < pixels.data.length; i += stride) {
      total++;
      if (pixels.data[i + 3] === 0) cleared++;
    }
    return total ? Math.round((cleared / total) * 100) : 0;
  };

  const handleStart = (e) => {
    if (!loaded || finished || hasCompletedRef.current) return;

    // First contact is what claims the card server-side, via onScratchStart.
    if (!hasStartedRef.current) {
      hasStartedRef.current = true;
      if (onScratchStart) onScratchStart();
    }

    isDrawingRef.current = true;
    lastPointRef.current = getPoint(e);
  };

  const handleMove = (e) => {
    if (!isDrawingRef.current || finished) return;
    e.preventDefault();
    const point = getPoint(e);
    const last = lastPointRef.current;
    const distance = Math.hypot(point.x - last.x, point.y - last.y);
    const angle = Math.atan2(point.y - last.y, point.x - last.x);
    for (let i = 0; i < distance; i += 4) {
      scratchAt({
        x: last.x + Math.cos(angle) * i,
        y: last.y + Math.sin(angle) * i,
      });
    }
    lastPointRef.current = point;

    // `finished` is state and updates a render behind, so a fast drag past the
    // threshold would re-enter this branch and fire onComplete several times
    // before the guard above catches up. hasCompletedRef flips synchronously so
    // onComplete fires exactly once per card.
    if (!hasCompletedRef.current && getClearedPercent() > finishPercent) {
      hasCompletedRef.current = true;
      isDrawingRef.current = false;
      setFinished(true);
      if (onComplete) onComplete();
    }
  };

  const handleEnd = () => {
    isDrawingRef.current = false;
  };

  return (
    <div
      style={{
        position: "relative",
        width,
        height,
        userSelect: "none",
        overflow: "hidden",
        borderRadius: 12,
        background: "#111744",
        border: "none",
        outline: "none",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width,
          height,
          visibility: loaded ? "visible" : "hidden",
        }}
      >
        {children}
      </div>
      {!finished && (
        <canvas
          aria-label="Scratch card cover"
          ref={canvasRef}
          width={width}
          height={height}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            touchAction: "none",
            cursor: "pointer",
          }}
          onMouseDown={handleStart}
          onMouseMove={handleMove}
          onMouseUp={handleEnd}
          onMouseLeave={handleEnd}
          onTouchStart={handleStart}
          onTouchMove={handleMove}
          onTouchEnd={handleEnd}
        />
      )}
    </div>
  );
};

export default ScratchCanvas;
